import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles, CircularProgress } from '@material-ui/core'
import { portfolioResearch } from '../../../../api/coreData'

const PAGE_SIZE = 50

const STATUS_LABELS = {
  investigating: 'Investigating',
  needs_input: 'Needs input',
  complete: 'Complete',
  inconclusive: 'Inconclusive',
  error: 'Error'
}

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    margin: '-14px -14px',
    padding: '14px 14px'
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '32px 0',
    color: 'var(--color-ai-700, #6b7280)',
    fontSize: 12
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '40px 24px',
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13,
    textAlign: 'center'
  },
  error: {
    color: 'var(--color-danger-600, #c62828)',
    fontSize: 13,
    padding: '16px 0',
    textAlign: 'center'
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '10px 12px',
    background: 'linear-gradient(135deg, var(--color-ai-050) 0%, var(--opal-surface-default, #fff) 100%)',
    border: '1px solid var(--color-ai-200)',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease',
    textAlign: 'left',
    appearance: 'none',
    font: 'inherit',
    color: 'inherit',
    '&:hover': {
      borderColor: 'var(--color-ai-400)',
      boxShadow: '0 1px 0 0 color-mix(in srgb, var(--color-ai-400) 25%, transparent), 0 4px 14px rgba(15,23,42,0.06)',
      transform: 'translateY(-1px)'
    },
    '&:focus-visible': {
      outline: '2px solid var(--color-ai-500)',
      outlineOffset: 2
    }
  },
  rowTopLine: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 8
  },
  rowTitle: {
    fontSize: 13,
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-neutral-900, #111827)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    flex: 1
  },
  rowTime: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #6b7280)',
    flexShrink: 0
  },
  rowSubject: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #6b7280)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  rowFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 10,
    fontWeight: 'var(--font-weight-bold, 700)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    padding: '2px 6px',
    borderRadius: 3,
    lineHeight: 1.3
  },
  chipInvestigating: {
    background: 'var(--color-ai-200)',
    color: 'var(--color-ai-700)',
    boxShadow: '0 0 0 1px color-mix(in srgb, var(--color-ai-400) 35%, transparent)'
  },
  chipNeedsInput: {
    background: 'var(--color-warning-100, #fef3c7)',
    color: 'var(--color-warning-800, #92400e)'
  },
  chipComplete: {
    background: 'var(--color-success-100, #d1fae5)',
    color: 'var(--color-success-800, #065f46)'
  },
  chipInconclusive: {
    background: 'var(--color-neutral-100, #f3f4f6)',
    color: 'var(--color-neutral-700, #374151)'
  },
  chipError: {
    background: 'var(--color-danger-100, #fee2e2)',
    color: 'var(--color-danger-800, #991b1b)'
  }
}))

export default function SessionList ({ onSelect }) {
  const classes = useStyles()
  const { data, isLoading, error } = portfolioResearch.useListSessions({
    take: PAGE_SIZE
  })

  if (isLoading && !data) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={18} />
        <span>Loading recent investigations&hellip;</span>
      </div>
    )
  }
  if (error) {
    return <div className={classes.error}>Could not load recent investigations.</div>
  }

  const sessions = data?.sessions ?? []
  if (sessions.length === 0) {
    return (
      <div className={classes.empty}>
        <span>No investigations yet.</span>
        <span>Right-click a row in the grid and pick &ldquo;Explain this row&rdquo; to start one.</span>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      {sessions.map(session => (
        <SessionRow
          key={session.portfolioResearchReportSessionId}
          session={session}
          onSelect={onSelect}
        />
      ))}
    </div>
  )
}

function SessionRow ({ session, onSelect }) {
  const classes = useStyles()
  const subject = session.subject || {}
  const primary = session.title || subject.rowName || `Investigation #${session.portfolioResearchReportSessionId}`
  const subjectLine = [subject.view, subject.asOfDate && `as of ${subject.asOfDate}`].filter(Boolean).join(' · ')
  const created = session.createdDate ? new Date(session.createdDate) : null

  return (
    <button
      type='button'
      className={classes.row}
      onClick={() => onSelect(session)}
    >
      <div className={classes.rowTopLine}>
        <span className={classes.rowTitle}>{primary}</span>
        {created && (
          <span className={classes.rowTime} title={created.toLocaleString()}>
            {formatRelative(created)}
          </span>
        )}
      </div>
      {subjectLine && <div className={classes.rowSubject}>{subjectLine}</div>}
      <div className={classes.rowFooter}>
        <StatusChip status={session.status} />
      </div>
    </button>
  )
}

function StatusChip ({ status }) {
  const classes = useStyles()
  const label = STATUS_LABELS[status] || status || 'Unknown'
  const variant = (
    status === 'investigating' ? classes.chipInvestigating
      : status === 'needs_input' ? classes.chipNeedsInput
        : status === 'complete' ? classes.chipComplete
          : status === 'error' ? classes.chipError
            : classes.chipInconclusive
  )
  return <span className={`${classes.chip} ${variant}`}>{label}</span>
}

function formatRelative (date) {
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000))
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}

const sessionShape = PropTypes.shape({
  portfolioResearchReportSessionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  status: PropTypes.string,
  title: PropTypes.string,
  subject: PropTypes.shape({
    rowName: PropTypes.string,
    view: PropTypes.string,
    asOfDate: PropTypes.string
  }),
  createdDate: PropTypes.string
})

SessionList.propTypes = {
  onSelect: PropTypes.func.isRequired
}

SessionRow.propTypes = {
  session: sessionShape.isRequired,
  onSelect: PropTypes.func.isRequired
}

StatusChip.propTypes = {
  status: PropTypes.string
}
